// The Resource limits form's translation layer: the API speaks millicores and
// "0 skips, -1 clears", the form speaks cores and empty-means-inherited.

// limitInputs renders the stored overrides as form values: an override shows
// its number (cores for CPU), an inherited field shows empty.
export const limitInputs = (overrides) => {
  const ov = overrides || {};
  return {
    memory: ov.memory_mb ? String(ov.memory_mb) : "",
    disk: ov.disk_mb ? String(ov.disk_mb) : "",
    cpu: ov.cpu_milli ? String(ov.cpu_milli / 1000) : "",
  };
};

// limitsPatchBody turns the form back into the PATCH body. An empty field
// means "no override": if one existed it is cleared (-1), else skipped (0).
// A non-numeric value is treated as skip rather than sent; the server floors
// real values anyway.
export const limitsPatchBody = (inputs, overrides) => {
  const ov = overrides || {};
  const field = (value, hadOverride, factor = 1) => {
    const trimmed = String(value ?? "").trim();
    if (trimmed === "") return hadOverride ? -1 : 0;
    const n = Math.round(parseFloat(trimmed) * factor);
    return Number.isFinite(n) && n > 0 ? n : 0;
  };
  return {
    memory_mb: field(inputs.memory, !!ov.memory_mb),
    disk_mb: field(inputs.disk, !!ov.disk_mb),
    cpu_milli: field(inputs.cpu, !!ov.cpu_milli, 1000),
  };
};

// customLimitError checks a typed-in value against the server's floor and the
// pool headroom, so the form explains a bad value instead of the server
// refusing it; "" means fine (an empty field is the default). MB are whole
// numbers; cores may be fractional.
export const customLimitError = (value, { min, max, unit }) => {
  const trimmed = String(value ?? "").trim();
  if (trimmed === "") return "";
  const n = Number(trimmed);
  if (!Number.isFinite(n)) return "Enter a number";
  if (unit !== "cores" && !Number.isInteger(n)) return `Enter a whole number of ${unit}`;
  if (n < min) return `At least ${min} ${unit}`;
  if (n > max) return `Over your pool: at most ${max} ${unit}`;
  return "";
};

// sizeToMB turns a typed size and its unit into the MB the API takes; text
// that is empty or not a number passes through for customLimitError to explain.
export const sizeToMB = (text, unit) => {
  const trimmed = String(text ?? "").trim();
  const n = Number(trimmed);
  if (trimmed === "" || !Number.isFinite(n)) return trimmed;
  return String(unit === "GB" ? Math.round(n * 1024) : n);
};

// sizeFromMB splits a stored size into what the custom field shows: whole
// gigabytes in GB, anything else in MB.
export const sizeFromMB = (mb) => {
  const n = Number(mb);
  if (mb !== "" && Number.isInteger(n) && n >= 1024 && n % 1024 === 0) return { text: String(n / 1024), unit: "GB" };
  return { text: String(mb ?? ""), unit: "MB" };
};
